import type { Cell, Workbook } from 'exceljs';

export interface ExportXLSXSheet {
  /** Worksheet name */
  name: string;
  rows: Record<string, unknown>[];
  /** Column headers that should receive right-aligned currency formatting */
  currencyColumns?: string[];
}

export interface ExportXLSXOptions {
  /** Worksheet name (default: 'Sheet1') */
  sheetName?: string;
  /** Column headers that should receive right-aligned currency formatting */
  currencyColumns?: string[];
  /** Maximum column width in characters (default: 40) */
  maxColWidth?: number;
  /**
   * Further worksheets appended after the first, for a page whose report is
   * more than one table.
   *
   * Optional and additive on purpose: this helper has sixteen callers and no
   * tests, so the default path had to stay byte-identical. Omit it and nothing
   * about the produced workbook changes. A sheet with no rows is skipped
   * rather than written empty.
   */
  extraSheets?: ExportXLSXSheet[];
}

/**
 * Serialise `rows` to a UTF-8 BOM CSV and trigger a browser download.
 * Uses papaparse — loaded lazily so it doesn't bloat the initial bundle.
 */
export async function exportRowsAsCSV(
  rows: Record<string, unknown>[],
  filename: string,
): Promise<void> {
  const { default: Papa } = await import('papaparse');
  const csv = '﻿' + Papa.unparse(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Serialise `rows` to a styled XLSX workbook and trigger a browser download.
 * Uses exceljs — loaded lazily so it doesn't bloat the initial bundle.
 *
 * Styling applied:
 *  - Dark header row (bold white text on #1E293B), frozen first row
 *  - Alternating white / #F8FAFC row background
 *  - Auto column widths (capped at `maxColWidth`, min 12)
 *  - Right-aligned "$#,##0.00" number format for any column listed in `currencyColumns`
 */
export async function exportRowsAsXLSX(
  rows: Record<string, unknown>[],
  filename: string,
  options: ExportXLSXOptions = {},
): Promise<void> {
  const {
    sheetName = 'Sheet1',
    currencyColumns = [],
    maxColWidth = 40,
    extraSheets = [],
  } = options;

  // Every sheet that actually has something in it, primary first.
  //
  // The bail is on "nothing anywhere", NOT on the primary table being empty.
  // Those stopped being the same statement once a workbook could carry more
  // than one sheet, and the difference is reachable: on the customer report
  // `topCustomers` is scoped to the selected period while the lead-source table
  // is all-time, so a quiet date range shows an empty customers table above a
  // populated LTV one. Bailing on the primary alone handed that user an Export
  // button that downloaded nothing.
  //
  // With no `extraSheets` this reduces to the original `rows.length === 0`
  // check, which is what keeps the sixteen existing callers unchanged.
  const sheets: ExportXLSXSheet[] = [
    { name: sheetName, rows, currencyColumns },
    ...extraSheets,
  ].filter((sheet) => sheet.rows.length > 0);
  if (sheets.length === 0) return;

  const mod = await import('exceljs');
  const ExcelJS = (mod as unknown as { default?: typeof mod }).default ?? mod;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Notifi';
  wb.created = new Date();

  for (const sheet of sheets) {
    addSheet(wb, sheet.name, sheet.rows, sheet.currencyColumns ?? [], maxColWidth);
  }

  // Download
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * One styled worksheet. Lifted out of `exportRowsAsXLSX` unchanged when
 * `extraSheets` arrived, so a multi-sheet workbook and a single-sheet one are
 * produced by exactly the same code.
 */
function addSheet(
  wb: Workbook,
  sheetName: string,
  rows: Record<string, unknown>[],
  currencyColumns: string[],
  maxColWidth: number,
): void {
  const ws = wb.addWorksheet(sheetName, { views: [{ state: 'frozen', ySplit: 1 }] });

  const headers = Object.keys(rows[0]);

  // Header row
  ws.addRow(headers);
  const headerRow = ws.getRow(1);
  headerRow.eachCell((cell: Cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FF334155' } } };
  });
  headerRow.height = 28;

  // Data rows
  rows.forEach((row, i) => {
    const dataRow = ws.addRow(Object.values(row));
    const isAlt = i % 2 === 1;
    dataRow.eachCell({ includeEmpty: true }, (cell: Cell, colNum: number) => {
      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: isAlt ? 'FFF8FAFC' : 'FFFFFFFF' },
      };
      const header = headers[colNum - 1];
      if (header !== undefined && currencyColumns.includes(header)) {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
        cell.numFmt = '"$"#,##0.00';
      }
    });
    dataRow.height = 22;
  });

  // Auto column widths.
  //
  // One pass over the rows, widening each column as it goes, rather than one
  // pass PER COLUMN. Two reasons, both of which bit at real catalog sizes:
  //
  //  - `Math.max(...rows.map(…))` spreads one argument per row. Past roughly
  //    65k rows that overflows the call stack and the export dies with a bare
  //    `RangeError` after the user has already waited through the fetch.
  //  - `Object.values(r)` inside a per-column loop rebuilt the row's value
  //    array once for EVERY column: a 3.7k x 14 catalog allocated ~52k arrays
  //    to measure text it had already walked.
  const widths = headers.map((h) => h.length);
  for (const row of rows) {
    const values = Object.values(row);
    for (let i = 0; i < widths.length; i++) {
      const len = String(values[i] ?? '').length;
      if (len > widths[i]) widths[i] = len;
    }
  }
  ws.columns.forEach((col, i) => {
    col.width = Math.min(Math.max((widths[i] ?? 0) + 4, 12), maxColWidth);
  });
}
